import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Queries
import { useTableDeleteRowMutation, TABLE_QUERY_KEY } from './tableQueries';

// Components
import useToast from '../../components/molecules/Toast/useToast';

/**
 * Custom hook for optimistic updates of table
 * @param {import('@tanstack/react-query').UseMutationOptions<boolean, Error, { id: string, deleteUrl: string }, unknown>} [options = {}] - The mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult} The mutation with optimistic updates
 */
export const useTableDeleteRowOptimistic = (options = {}) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = options;

  return useTableDeleteRowMutation({
    ...restOptions,
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });

      /**
       * @type {TableQueryDataTuple[]}
       */
      const arrayTableData = /** @type {TableQueryDataTuple[]} */ (queryClient.getQueriesData({
        queryKey: [TABLE_QUERY_KEY],
        type: 'active',
      }));

      const activeTableData = arrayTableData?.[0]?.[1];
      if (!activeTableData) return;

      const optimisticTableData = {
        ...activeTableData,
        groups: activeTableData.groups.map((group) => ({
          ...group,
          data: group.data.map((row) => {
            const shouldDelete = row.id === id;
            return shouldDelete
              ? { ...row, isDeletingOptimistic: true }
              : row;
          }),
        })),
      };

      // Update the cache with optimistic data
      const queryKey = arrayTableData[0]?.[0];
      if (queryKey) {
        queryClient.setQueryData(queryKey, optimisticTableData);
      }
    },
    onError: (error, variables, context) => {
      notify({
        message: error?.message || t('toast.table.error'),
        type: 'alert',
      });
      onError?.(error, variables, context);
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
      onSettled?.(data, error, variables);
    },
  });
};
